"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Field, Input, FormStatus, SubmitButton } from "@/components/ui/Form";
import { Icon } from "@/lib/icons/Icon";
import styles from "./AvatarUploadModal.module.css";
import { ArrowIcon } from "@/components/ui/ArrowIcon/ArrowIcon";

/**
 * ChangeEmailModal — zmiana adresu email przez 2-stage flow:
 *
 * **Stage "form":** RHF + zod form `{new_email, current_password}`.
 *   - Re-auth obecnym hasłem (defense against session hijack)
 *   - Validate email format + match potwierdzenia
 *
 * **Stage "sent":** info "wysłano emaile" po success init.
 *   - User widzi masked adresy (nowy + stary)
 *   - Status notifications (confirm + warning)
 *   - Instrukcja: kliknij link w nowym adresie (1h expiry)
 *
 * **Custom URLs:** wysyłamy do backendu `custom_verify_url` + `custom_cancel_url`
 * z placeholderami `{token}` `{id_user}`. Backend podstawia -> user dostaje
 * linki do `/konto?action=confirm-change-email&token=X&user_id=Y` (zamiast
 * default ILINK do legacy Smarty page).
 */
const changeEmailSchema = z
  .object({
    new_email: z
      .string()
      .min(1, "Podaj nowy adres email.")
      .email("Niepoprawny format adresu email."),
    new_email_confirm: z.string().min(1, "Potwierdź nowy adres email."),
    current_password: z.string().min(1, "Podaj aktualne hasło."),
  })
  .refine((v) => v.new_email.toLowerCase() === v.new_email_confirm.toLowerCase(), {
    message: "Adresy email nie zgadzają się.",
    path: ["new_email_confirm"],
  });

type ChangeEmailFormValues = z.infer<typeof changeEmailSchema>;

export function ChangeEmailModal({
  currentEmail,
  onClose,
  onSuccess,
}: {
  currentEmail: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [stage, setStage] = useState<"form" | "sent">("form");
  const [topError, setTopError] = useState<string | null>(null);
  const [sentResult, setSentResult] = useState<InitChangeEmailResponse | null>(
    null,
  );

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ChangeEmailFormValues>({
    resolver: zodResolver(changeEmailSchema),
    mode: "onTouched",
    defaultValues: { new_email: "", new_email_confirm: "", current_password: "" },
  });

  /* SSR-safe portal mount (zob. AvatarUploadModal). */
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  /* Mouse-down target tracking dla backdrop close — naprawia bug "drag-from-inside-out
     closes modal". Bez tego: użytkownik zaznacza tekst w inpucie (mousedown wewnątrz)
     -> przeciąga poza modal -> puszcza (mouseup na backdrop) -> `onClick` firuje na
     backdrop (browser fires click na common ancestor mousedown/mouseup) -> modal się
     zamyka. Fix: zamykamy TYLKO gdy mousedown ALSO był na backdrop. */
  const backdropMouseDownTargetRef = useRef<EventTarget | null>(null);

  /* Esc -> close (gdy nie busy). */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isSubmitting) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isSubmitting, onClose]);

  const onSubmit = async (values: ChangeEmailFormValues) => {
    setTopError(null);

    /* Custom URLs dla linków w emailach — frontend wie gdzie user wraca po
       kliknięciu. Backend podstawia `{token}` + `{id_user}` (encrypted)
       placeholders. 3-stage flow: approve (OLD klika) -> confirm (NEW klika)
       -> done. Plus cancel dla obu stanów. */
    const origin = window.location.origin;
    const pathname = window.location.pathname;
    const customApproveUrl = `${origin}${pathname}?action=approve-change-email&token={token}&user_id={id_user}`;
    const customConfirmUrl = `${origin}${pathname}?action=confirm-change-email&token={token}&user_id={id_user}`;
    const customCancelUrl = `${origin}${pathname}?action=cancel-change-email&token={token}&user_id={id_user}`;

    try {
      const res = await fetch("/api/iiapi/user/change-email/init", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          new_email: values.new_email,
          current_password: values.current_password,
          /* `custom_verify_url` = confirm link (używany dopiero w stage 2 po approve) */
          custom_verify_url: customConfirmUrl,
          custom_cancel_url: customCancelUrl,
          custom_approve_url: customApproveUrl,
        }),
      });
      const body = (await res.json().catch(() => null)) as
        | (InitChangeEmailResponse & { ok?: boolean; message?: string })
        | null;

      if (!res.ok || !body?.ok) {
        const code = (body as { code?: number } | null)?.code;
        /* Per-pole mapping dla wybranych error codes z backendu.
           Kody initChangeEmail: 2120120-2120129 (dawne 2001-2010 - przenumerowane przy
           standaryzacji IERROR::getCode, zob. web/IMPERIAL/IERROR.php). */
        if (code === 2120126) {
          /* Wrong password - pokaż pod password input. */
          setError("current_password", {
            type: "server",
            message: body?.message ?? "Nieprawidłowe hasło.",
          });
          return;
        }
        if (code === 2120123) {
          /* Email taken. */
          setError("new_email", {
            type: "server",
            message: body?.message ?? "Adres email jest już zajęty.",
          });
          return;
        }
        setTopError(body?.message ?? "Nie udało się zainicjować zmiany emaila.");
        return;
      }

      setSentResult(body);
      setStage("sent");
    } catch (e) {
      setTopError(
        e instanceof Error ? `Błąd sieci: ${e.message}` : "Błąd sieci.",
      );
    }
  };

  if (!mounted) return null;

  return createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => {
        /* Track gdzie zaczął się click — żeby drag-z-input'a-poza-modal
           NIE zamykał modala. */
        backdropMouseDownTargetRef.current = e.target;
      }}
      onClick={(e) => {
        /* Close TYLKO gdy OBA — mousedown i click target — to backdrop sam.
           Drag z input'a do backdrop'a -> mousedown był na inpucie -> skip. */
        if (
          e.target === e.currentTarget &&
          backdropMouseDownTargetRef.current === e.currentTarget &&
          !isSubmitting
        ) {
          onClose();
        }
        backdropMouseDownTargetRef.current = null;
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Zmiana adresu email"
    >
      <div className={styles.modal}>
        <header className={styles.header}>
          <h2 className={styles.title}>
            {stage === "form" ? "Zmiana adresu email" : "Sprawdź skrzynkę"}
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Zamknij"
          >
            <Icon name="x" />
          </button>
        </header>

        <div className={styles.body}>
          {isSubmitting && (
            <div className={styles.loadingOverlay} aria-busy="true" role="status">
              <div className={styles.spinner} aria-hidden="true" />
              <p className={styles.loadingText}>Wysyłanie maili weryfikacyjnych…</p>
            </div>
          )}

          {stage === "form" && (
            <form
              onSubmit={handleSubmit(onSubmit)}
              noValidate
              style={{ display: "flex", flexDirection: "column", gap: "var(--space-md)" }}
            >
              <p className={styles.loadingText} style={{ textAlign: "left" }}>
                Obecny email: <strong>{currentEmail}</strong>
              </p>

              <Field
                label="Nowy adres email"
                required
                error={errors.new_email?.message}
              >
                <Input
                  type="email"
                  autoComplete="email"
                  disabled={isSubmitting}
                  {...register("new_email")}
                />
              </Field>

              <Field
                label="Potwierdź nowy adres email"
                required
                error={errors.new_email_confirm?.message}
              >
                <Input
                  type="email"
                  autoComplete="email"
                  disabled={isSubmitting}
                  {...register("new_email_confirm")}
                />
              </Field>

              <Field
                label="Aktualne hasło"
                required
                error={errors.current_password?.message}
                hint="Wymagane dla bezpieczeństwa — potwierdzenie że to Ty inicjujesz zmianę."
              >
                <Input
                  type="password"
                  autoComplete="current-password"
                  disabled={isSubmitting}
                  {...register("current_password")}
                />
              </Field>

              {topError && <FormStatus variant="error">{topError}</FormStatus>}

              <FormStatus variant="info">
                <strong>Procedura 3-stopniowa (podwójna zgoda):</strong>
                <br />1. Wysłanie linka „Wyraź zgodę" oraz „Anuluj zmianę" na <strong>obecny</strong> adres email,
                <br />2. Po wyrażeniu zgody z obecnego adresu <ArrowIcon spacing={false} size={14} /> link „Potwierdź" na <strong>nowy</strong> adres,
                <br />3. Po potwierdzeniu z nowego adresu <ArrowIcon spacing={false} size={14} /> zmiana aktywowana.
                <br /><br />
                Każdy etap ma <strong>1 godzinę</strong> na reakcję. Po finalnej zmianie <strong>wszystkie sesje na innych urządzeniach zostaną wylogowane</strong>.
              </FormStatus>

              <div className={styles.footer} style={{ marginTop: "var(--space-sm)" }}>
                <button
                  type="button"
                  className={styles.linkBtn}
                  onClick={onClose}
                  disabled={isSubmitting}
                >
                  Anuluj
                </button>
                <SubmitButton loading={isSubmitting}>Wyślij linki weryfikacyjne</SubmitButton>
              </div>
            </form>
          )}

          {stage === "sent" && sentResult && (
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-md)" }}>
              <FormStatus variant="success">
                Wysłano email na obecny adres.
              </FormStatus>

              <div>
                <p
                  className={styles.loadingText}
                  style={{
                    textAlign: "left",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Icon name="envelope" aria-hidden />
                  <span>
                    Link „Wyraź zgodę" + „Anuluj zmianę" wysłany na obecny adres:{" "}
                    <strong>{sentResult.old_email_masked}</strong>
                  </span>
                  {!sentResult.notifications.approve.sent && (
                    <>
                      <Icon name="x-circle" aria-hidden />
                      <span>(nie udało się wysłać)</span>
                    </>
                  )}
                </p>
              </div>

              <FormStatus variant="warning">
                <strong>Następne kroki:</strong>
                <br />1. Otwórz skrzynkę <strong>obecnego</strong> adresu (sprawdź też folder <strong>SPAM</strong> / wiadomości niechciane) i kliknij link <strong>„Wyraź zgodę"</strong> w mailu — masz na to 1 godzinę.
                <br />2. Po wyrażeniu zgody wyślemy email weryfikacyjny na <strong>nowy</strong> adres (też 1h ważności).
                <br />3. Kliknij link <strong>„Potwierdź"</strong> w mailu na nowym adresie — wtedy zmiana wchodzi w życie.
                <br /><br />
                Po finalnej zmianie zostaniesz wylogowany na wszystkich urządzeniach.
              </FormStatus>

              <div className={styles.footer} style={{ marginTop: "var(--space-sm)" }}>
                <SubmitButton onClick={onSuccess}>Rozumiem, zamknij</SubmitButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

type InitChangeEmailResponse = {
  state: "pending_approval";
  expires_in: number;
  expiry_date: string;
  new_email_masked: string;
  old_email_masked: string;
  notifications: {
    /* Nowy 3-stage flow: tylko 1 email przy init (do OLD z approve+cancel).
       Confirm email wysyłany dopiero w stage 2 (po approve). */
    approve: { sent: boolean; to_masked: string };
  };
};
